'use client';

import { useEffect, useState } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // 记录详细的全局错误信息
    console.group('🔴 Global Application Error');
    console.error('Error:', error);
    console.error('Message:', error.message);
    console.error('Name:', error.name);
    console.error('Stack:', error.stack);
    if (error.digest) {
      console.error('Digest:', error.digest);
    }
    console.groupEnd();

    // 可以集成错误监控服务
    // if (typeof window !== 'undefined') {
    //   Sentry.captureException(error);
    // }
  }, [error]);

  const copyErrorToClipboard = () => {
    const errorText = `
全局应用错误：
类型: ${error.name}
消息: ${error.message}
${error.digest ? `错误 ID: ${error.digest}` : ''}
时间: ${new Date().toISOString()}

堆栈跟踪:
${error.stack || '无堆栈信息'}

页面: ${window.location.href}
用户代理: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <html lang="zh-CN">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>应用错误</title>
      </head>
      <body style={{ margin: 0, padding: 0, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
          padding: '24px',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '56rem',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>
                ⚠️
              </div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#111827',
                marginBottom: '12px',
                marginTop: 0,
              }}>
                应用遇到严重错误
              </h1>
              <p style={{
                color: '#6b7280',
                fontSize: '16px',
                margin: 0,
              }}>
                应用在根层级遇到了一个错误。下方显示了详细的错误信息，可以帮助定位问题。
              </p>
            </div>

            {/* 错误基本信息 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#111827',
                marginTop: 0,
                marginBottom: '16px',
              }}>
                错误信息
              </h2>
              <div style={{ marginBottom: '16px' }}>
                <strong style={{ color: '#374151' }}>错误类型：</strong>
                <span style={{
                  marginLeft: '8px',
                  color: '#dc2626',
                  fontFamily: 'monospace',
                }}>
                  {error.name}
                </span>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong style={{ color: '#374151' }}>错误消息：</strong>
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                }}>
                  <p style={{
                    color: '#b91c1c',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    margin: 0,
                    wordBreak: 'break-word',
                  }}>
                    {error.message}
                  </p>
                </div>
              </div>
              {error.digest && (
                <div style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#374151' }}>错误 ID：</strong>
                  <span style={{
                    marginLeft: '8px',
                    color: '#6b7280',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                  }}>
                    {error.digest}
                  </span>
                </div>
              )}
              <div>
                <strong style={{ color: '#374151' }}>发生时间：</strong>
                <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                  {new Date().toLocaleString('zh-CN')}
                </span>
              </div>
            </div>

            {/* 堆栈跟踪 */}
            {error.stack && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <details open={process.env.NODE_ENV === 'development'}>
                  <summary style={{
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '16px',
                    listStyle: 'none',
                  }}>
                    📍 堆栈跟踪 (点击展开/收起)
                  </summary>
                  <div style={{
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    borderRadius: '6px',
                    padding: '16px',
                    overflowX: 'auto',
                    marginBottom: '12px',
                  }}>
                    <pre style={{
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                    }}>
                      {error.stack}
                    </pre>
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0,
                  }}>
                    💡 提示：堆栈跟踪显示了错误发生的具体位置和调用路径。查看第一行通常能找到错误发生的源文件和行号。
                  </p>
                </details>
              </div>
            )}

            {/* 调试建议 */}
            <div style={{
              backgroundColor: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e3a8a',
                marginTop: 0,
                marginBottom: '12px',
              }}>
                🔍 调试建议
              </h3>
              <ul style={{
                color: '#1e40af',
                fontSize: '14px',
                paddingLeft: '24px',
                margin: 0,
                lineHeight: '1.8',
              }}>
                <li>查看上方的堆栈跟踪，找到错误发生的具体文件和行号</li>
                <li>打开浏览器开发者工具（F12），查看 Console 标签页的详细错误信息</li>
                <li>这是一个全局错误，可能影响了应用的根布局</li>
                <li>点击下方&ldquo;复制错误信息&rdquo;按钮，将完整错误信息发送给技术支持</li>
              </ul>
            </div>

            {/* 操作按钮 */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={reset}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                🔄 重新加载
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                🏠 返回首页
              </button>
              <button
                onClick={copyErrorToClipboard}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                {copied ? '✓ 已复制' : '📋 复制错误信息'}
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
